import { useGT, useMessages } from 'gt-react';
import type {
  PricelistCaseFileRoleDTO,
  PricelistCaseSeedField,
} from '@erp/rpc';
import {
  PRICELIST_CASE_AUDITED_FIELDS,
  resolveWaitingOnLabel,
  type PricelistCaseAuditedField,
} from '@erp/shared-types';
import { extraAuditFieldLabel } from './pricelistCaseAuditLabels';
import { usePricelistCaseWaitingOnOptions } from './pricelistCaseWaitingOnOptions';

/**
 * Every label a pricelist update case shows for one of its own vocabularies:
 * the file roles, the seeded domain fields, and the audited columns the
 * timeline names.
 *
 * One module because all three are the SAME field set seen three ways — a
 * seeded `effectiveDate`, an audited `effective_date` and the card's
 * "Effective date" must read identically, and three separate label tables
 * would drift on the first rewording. The card's own field labels stay in
 * `pricelistDetailFields`, which is where they are also the `<Label>`s of the
 * inputs.
 */

/**
 * A case's stored `waitingOnKey` as a label — THE resolver for every surface
 * that shows what a case is parked on (the detail page's field, the timeline's
 * before/after values, and the cases list column).
 *
 * Resolved live from the org's option list at render, never snapshotted onto
 * the case: relabelling a reason must change every case already parked under
 * it. Disabled options still resolve (they leave the picker, not history) and
 * a key that no longer exists in the config at all degrades to the raw key
 * rather than a blank cell.
 *
 * Returns null for an unparked case so each surface writes its own empty state
 * ("Not parked" on the detail page, an em dash in a table cell) instead of
 * this module guessing which one a caller wants.
 */
export function usePricelistCaseWaitingOnLabel(): (
  key: string | null,
) => string | null {
  const options = usePricelistCaseWaitingOnOptions();
  return (key) => (key === null ? null : resolveWaitingOnLabel(key, options));
}

/** What each file contributes to the case. */
export function useCaseFileRoleLabel(): (
  role: PricelistCaseFileRoleDTO,
) => string {
  const gt = useGT();
  return (role) => {
    switch (role) {
      case 'notice':
        return gt('Notice', {
          context:
            "pricelist case file role: the vendor's advance notice of a price change",
        });
      case 'price_file':
        return gt('Price file', {
          context: 'pricelist case file role: the price data itself',
        });
      case 'supporting':
        return gt('Supporting', {
          context:
            'pricelist case file role: a document that is neither the notice nor the price file',
        });
    }
  };
}

/**
 * The domain fields, keyed as the API spells them.
 *
 * Deliberately a lookup built from a `switch` rather than a record literal:
 * the compiler then requires a label for every member of the union, so adding
 * a seedable field cannot ship with a blank row in the dialog.
 */
export function useCaseFieldLabel(): (field: PricelistCaseSeedField) => string {
  const gt = useGT();
  return (field) => {
    switch (field) {
      case 'effectiveDate':
        return gt('Effective date', {
          context: "label for the date a price change's new prices take effect",
        });
      case 'erpUploadDate':
        return gt('AS/400 upload date', {
          context: 'label for the date a pricelist was loaded into the AS/400',
        });
      case 'receivedAs':
        return gt('Received as', {
          context: 'label for what the vendor sent for a price change',
        });
      case 'receivedDate':
        return gt('Received date', {
          context: 'label for the date a price change arrived from the vendor',
        });
      case 'divisionPriceChanges':
        return gt('Price change by division', {
          context: 'label for the per-division price change percentages',
        });
      case 'notes':
        return gt('Notes', { context: 'label for pricelist case notes' });
      case 'fromTariffChange':
        return gt('Resulting from tariff change', {
          context: 'label for the tariff-driven price change flag',
        });
      case 'anticipatedVendorChangePercent':
        return gt('Anticipated change', {
          context:
            'short label for the vendor-stated anticipated change (free text, e.g. a range) from the price change notification',
        });
    }
  };
}

/**
 * Snake_case audited column → the camelCase field key, so the timeline can
 * borrow {@link useCaseFieldLabel} instead of carrying a second label table.
 *
 * The audited columns with no seeded counterpart (`company_id`,
 * `waiting_on_key` and `follow_up_at`) resolve to null and are labelled
 * directly below — they are real fields a person changes, just not ones the
 * create flow seeds from a file.
 */
const SEED_FIELD_BY_AUDITED_COLUMN: Record<
  PricelistCaseAuditedField,
  PricelistCaseSeedField | null
> = {
  company_id: null,
  company_ids: null,
  vendor_numbers: null,
  effective_date: 'effectiveDate',
  received_as: 'receivedAs',
  received_date: 'receivedDate',
  erp_upload_date: 'erpUploadDate',
  division_price_changes: 'divisionPriceChanges',
  notes: 'notes',
  from_tariff_change: 'fromTariffChange',
  anticipated_vendor_change_percent: 'anticipatedVendorChangePercent',
  vendor_code: null,
  national_im_comparing_comments: null,
  upload_comments: null,
  waiting_on_key: null,
  follow_up_at: null,
  files_ready_at: null,
};

function isAuditedField(field: string): field is PricelistCaseAuditedField {
  return (PRICELIST_CASE_AUDITED_FIELDS as readonly string[]).includes(field);
}

export function isPricelistCaseVendorAuditField(field: string): boolean {
  return field === 'company_id' || field === 'company_ids';
}

/** What the timeline calls one changed column. */
export function useCaseAuditFieldLabel(): (field: string) => string {
  const gt = useGT();
  const messages = useMessages();
  const fieldLabel = useCaseFieldLabel();
  return (field) => {
    const seedField = isAuditedField(field)
      ? SEED_FIELD_BY_AUDITED_COLUMN[field]
      : null;
    if (seedField) return fieldLabel(seedField);
    if (isPricelistCaseVendorAuditField(field)) {
      return gt('Vendor', {
        context: 'label for the vendor a price change is from',
      });
    }
    const extra = extraAuditFieldLabel(field);
    if (extra) return messages(extra);
    return field;
  };
}
